import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { X, Megaphone, Rocket, AlertTriangle, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const STORAGE_KEY = 'equipr_dismissed_announcements';

const getDismissed = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch { return []; }
};

const dismiss = (id) => {
  const list = getDismissed();
  if (!list.includes(id)) {
    list.push(id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  }
};

const TYPE_STYLES = {
  release_notes: { icon: Rocket, bannerBg: 'bg-blue-600', bannerText: 'text-white', label: 'Release Notes' },
  announcement: { icon: Megaphone, bannerBg: 'bg-[#ffd108]', bannerText: 'text-black', label: 'Announcement' },
  maintenance: { icon: AlertTriangle, bannerBg: 'bg-amber-500', bannerText: 'text-black', label: 'Maintenance' },
};

const AnnouncementBanner = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [popupItem, setPopupItem] = useState(null);
  const [dismissedBanners, setDismissedBanners] = useState([]);

  useEffect(() => {
    if (!api || !user) return;
    const fetchAnnouncements = async () => {
      try {
        const res = await api.get('/announcements/active');
        const dismissed = getDismissed();
        const items = res.data || [];
        setAnnouncements(items);

        // Show first undismissed popup
        const popup = items.find(
          (a) => (a.display_mode === 'popup' || a.display_mode === 'both') && !dismissed.includes(a.id)
        );
        if (popup) setPopupItem(popup);
      } catch (err) {
        // Silent fail — announcements are non-critical
      }
    };
    fetchAnnouncements();
  }, [api, user]);

  const dismissPopup = () => {
    if (popupItem) {
      dismiss(popupItem.id);
      setPopupItem(null);
    }
  };

  const dismissBanner = (id) => {
    dismiss(id);
    setDismissedBanners((prev) => [...prev, id]);
  };

  const dismissed = getDismissed();
  const bannerItems = announcements.filter(
    (a) => (a.display_mode === 'banner' || a.display_mode === 'both') && !dismissed.includes(a.id) && !dismissedBanners.includes(a.id)
  );

  const typeStyle = (type) => TYPE_STYLES[type] || TYPE_STYLES.announcement;

  return (
    <>
      {/* Top banners */}
      {bannerItems.map((item) => {
        const style = typeStyle(item.type);
        const Icon = style.icon;
        return (
          <div
            key={item.id}
            className={`${style.bannerBg} ${style.bannerText} px-4 py-2 flex items-center justify-between text-sm`}
            data-testid={`announcement-banner-${item.id}`}
          >
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Icon className="h-4 w-4 shrink-0" />
              <span className="font-medium truncate">{item.title}</span>
              <Button
                variant="ghost"
                size="sm"
                className={`${style.bannerText} hover:opacity-80 h-6 px-2 text-xs`}
                onClick={() => { setPopupItem(item); }}
              >
                Read more <ChevronRight className="h-3 w-3 ml-0.5" />
              </Button>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className={`${style.bannerText} hover:opacity-70 h-6 w-6 p-0 shrink-0`}
              onClick={() => dismissBanner(item.id)}
              data-testid={`dismiss-banner-${item.id}`}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        );
      })}

      {/* Popup modal */}
      <Dialog open={!!popupItem} onOpenChange={(open) => { if (!open) dismissPopup(); }}>
        <DialogContent className="sm:max-w-[550px] max-h-[80vh] overflow-y-auto bg-[#1a1a2e] border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white text-lg flex items-center gap-2">
              {popupItem && (() => {
                const style = typeStyle(popupItem.type);
                const Icon = style.icon;
                return <Icon className="h-5 w-5 text-[#ffd108]" />;
              })()}
              {popupItem?.title}
            </DialogTitle>
          </DialogHeader>
          {popupItem && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Badge className={TYPE_STYLES[popupItem.type]?.bannerBg === 'bg-blue-600' ? 'bg-blue-500/20 text-blue-400' : popupItem.type === 'maintenance' ? 'bg-amber-500/20 text-amber-400' : 'bg-[#ffd108]/20 text-[#ffd108]'}>
                  {TYPE_STYLES[popupItem.type]?.label}
                </Badge>
                <span className="text-xs text-slate-400">
                  {popupItem.created_at && format(new Date(popupItem.created_at), 'MMM d, yyyy')}
                </span>
              </div>
              <div
                className="prose prose-sm prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: popupItem.body }}
              />
              <div className="flex justify-between items-center mt-6 pt-4 border-t border-slate-700">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-slate-400 hover:text-white text-xs"
                  onClick={() => { dismissPopup(); navigate('/release-notes'); }}
                >
                  View all release notes
                </Button>
                <Button
                  onClick={dismissPopup}
                  className="bg-[#ffd108] text-black hover:bg-[#e6bc07]"
                  data-testid="announcement-popup-dismiss"
                >
                  Got it
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AnnouncementBanner;
